import { useState, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useClickSound } from '../hooks/useClickSound';
import { getEvents, type LumaEvent } from '../utils/api';

interface EventsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile?: boolean;
}

export default function EventsPanel({ isOpen, onClose, isMobile = false }: EventsPanelProps) {
  const { withClickSound } = useClickSound();
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showHiddenCategories, setShowHiddenCategories] = useState<boolean>(false);

  // Fetch upcoming events to determine which categories have events
  const { data: upcomingData } = useQuery({
    queryKey: ['events', { upcoming: true }],
    queryFn: () => getEvents({ includePast: false }),
    staleTime: 5 * 60 * 1000,
    enabled: isOpen,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['events', { categoryFilter }],
    queryFn: () =>
      getEvents({
        tag: categoryFilter === 'all' ? undefined : categoryFilter,
        includePast: categoryFilter !== 'all',
      }),
    staleTime: 5 * 60 * 1000,
    enabled: isOpen,
  });

  const events: LumaEvent[] = data?.events || [];
  const upcomingEvents: LumaEvent[] = upcomingData?.events || [];

  // Categories: use original-ish key for filtering, cleaned label for display
  const { allCategories, visibleCategories, hiddenCategories } = useMemo(
    () => {
      const COUNTRY_LABELS = [
        'Balkans',
        'Brazil',
        'Canada',
        'Germany',
        'Georgia',
        'Global',
        'India',
        'Indonesia',
        'Ireland',
        'Japan',
        'Kazakhstan',
        'Korea',
        'Malaysia',
        'Netherlands',
        'Nigeria',
        'Poland',
        'Singapore',
        'Spain',
        'UAE',
        'UK',
        'Ukraine',
      ];

      const map = new Map<string, string>();

      const add = (raw?: string) => {
        if (!raw) return;
        const key = raw.trim();
        if (!key) return;
        if (key.toLowerCase() === 'personal') return; // hide Personal

        let label = key;
        if (key.startsWith('Superteam ') && key !== 'Superteam') {
          label = key.replace(/^Superteam\s+/i, '');
        }
        if (!map.has(key)) {
          map.set(key, label);
        }
      };

      // Add categories from all events (for when filter is active)
      events.forEach(ev => {
        add(ev.superteam);
        add(ev.category);
      });

      // Always include known country labels
      COUNTRY_LABELS.forEach(label => {
        if (label.toLowerCase() === 'personal') return;
        if (!map.has(label)) {
          map.set(label, label);
        }
      });

      const all = Array.from(map.entries())
        .map(([key, label]) => ({ key, label }))
        .sort((a, b) => a.label.localeCompare(b.label));

      // Determine which categories have upcoming events
      const categoriesWithUpcoming = new Set<string>();
      upcomingEvents.forEach(ev => {
        const superteam = ev.superteam?.toLowerCase() || '';
        const category = ev.category?.toLowerCase() || '';
        const tags = (ev.tags || []).map(t => t.toLowerCase());
        
        all.forEach(cat => {
          const catKey = cat.key.toLowerCase();
          if (
            superteam === catKey ||
            category === catKey ||
            superteam.includes(catKey) ||
            category.includes(catKey) ||
            tags.includes(catKey)
          ) {
            categoriesWithUpcoming.add(cat.key);
          }
        });
      });

      const visible = all.filter(cat => categoriesWithUpcoming.has(cat.key));
      const hidden = all.filter(cat => !categoriesWithUpcoming.has(cat.key));

      return { allCategories: all, visibleCategories: visible, hiddenCategories: hidden };
    },
    [events, upcomingEvents]
  );

  const filteredEvents = useMemo(() => {
    let list = events.filter(ev => ev.url && !ev.url.includes('/superteam'));

    // Backend already applies tag filter when categoryFilter !== 'all'.
    // We still sort and keep the same client-side ordering.
    list.sort((a, b) => {
      const aWhen = a.startAt || a.date;
      const bWhen = b.startAt || b.date;
      if (!aWhen && !bWhen) return 0;
      if (!aWhen) return 1;
      if (!bWhen) return -1;
      return new Date(aWhen).getTime() - new Date(bWhen).getTime();
    });
    return list;
  }, [events, categoryFilter]);

  // Group events by day (Today, Tomorrow, or date label)
  const dayGroups = useMemo(() => {
    const groups: { label: string; events: LumaEvent[] }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const msPerDay = 24 * 60 * 60 * 1000;

    const getLabel = (d: Date) => {
      const diffDays = Math.round((d.getTime() - today.getTime()) / msPerDay);
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Tomorrow';
      const dateStr = d.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
      });
      const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
      return `${dateStr} ${weekday}`;
    };

    for (const ev of filteredEvents) {
      const when = ev.startAt || ev.date;
      if (!when) continue;
      const d = new Date(when);
      const label = getLabel(d);
      let group = groups.find(g => g.label === label);
      if (!group) {
        group = { label, events: [] };
        groups.push(group);
      }
      group.events.push(ev);
    }

    return groups;
  }, [filteredEvents]);

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.45)',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    zIndex: 200,
    opacity: isOpen ? 1 : 0,
    pointerEvents: isOpen ? 'auto' : 'none',
    transition: 'opacity 0.3s ease',
  };

  const panelStyle: CSSProperties = {
    position: 'fixed',
    zIndex: 201,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'rgba(10, 10, 20, 0.95)',
    backdropFilter: 'blur(30px)',
    WebkitBackdropFilter: 'blur(30px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    ...(isMobile ? {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: 0,
      maxHeight: '100vh',
      transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
    } : {
      top: isOpen ? '80px' : '70px',
      left: '16px',
      right: '16px',
      maxWidth: '640px',
      width: '540px',
      bottom: '70px',
      maxHeight: 'calc(100vh - 140px)',
      borderRadius: '20px',
      transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(-20px) scale(0.95)',
      opacity: isOpen ? 1 : 0,
      pointerEvents: isOpen ? 'auto' : 'none',
    }),
  };

  const buttonStyle: CSSProperties = {
    marginTop: '12px',
    padding: '12px 18px',
    borderRadius: '999px',
    border: '1px solid rgba(153, 69, 255, 0.6)',
    background: 'linear-gradient(135deg, rgba(153,69,255,0.18), rgba(20,241,149,0.12))',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  };

  const handleOpenLuma = withClickSound(() => {
    window.open('https://luma.com/superteam', '_blank', 'noopener,noreferrer');
  });
  const headerLinkStyle: CSSProperties = {
    marginTop: '6px',
    fontSize: '11px',
    color: '#9f7cff',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationThickness: '1px',
  };


  return (
    <>
      <div style={backdropStyle} onClick={onClose} />

      <div data-events-panel style={panelStyle}>
        <div
          style={{
            flexShrink: 0,
            padding: '20px',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #9945FF, #14F195)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                }}
              >
                📅
              </div>
              <div>
                <h2
                  style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: 'white',
                    margin: 0,
                  }}
                >
                  Superteam Events
                </h2>
                <p
                  style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.55)',
                    margin: '4px 0 0',
                  }}
                >
                  Live event schedule is hosted on Luma. This list is a preview.
                </p>
                <button type="button" style={headerLinkStyle} onClick={handleOpenLuma}>
                  Open full calendar on Luma
                </button>
              </div>
            </div>

            <button
              onClick={withClickSound(onClose)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
              }}
            >
              ×
            </button>
          </div>

          {allCategories.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
              <button
                type="button"
                style={{
                  ...buttonStyle,
                  padding: '6px 12px',
                  borderRadius: '10px',
                  fontSize: '12px',
                }}
                onClick={withClickSound(() => setCategoryFilter('all'))}
              >
                All
              </button>
              {visibleCategories.map(cat => (
                <button
                  key={cat.key}
                  type="button"
                  style={{
                    ...buttonStyle,
                    padding: '6px 12px',
                    borderRadius: '10px',
                    fontSize: '12px',
                  }}
                  onClick={withClickSound(() => setCategoryFilter(cat.key))}
                >
                  {cat.label}
                </button>
              ))}
              {hiddenCategories.length > 0 && (
                <>
                  <button
                    type="button"
                    style={{
                      ...buttonStyle,
                      padding: '6px 12px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.15)',
                    }}
                    onClick={withClickSound(() => setShowHiddenCategories(!showHiddenCategories))}
                  >
                    {showHiddenCategories ? '−' : `+${hiddenCategories.length}`}
                  </button>
                  {showHiddenCategories && hiddenCategories.map(cat => (
                    <button
                      key={cat.key}
                      type="button"
                      style={{
                        ...buttonStyle,
                        padding: '6px 12px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        opacity: 0.6,
                      }}
                      onClick={withClickSound(() => setCategoryFilter(cat.key))}
                    >
                      {cat.label}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Events list preview */}
        <div
          className="events-scroll"
          style={{
            flex: 1,
            padding: '10px 20px 12px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {isLoading && (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: 'rgba(255,255,255,0.5)',
              }}
            >
              Loading events preview...
            </div>
          )}

          {error && (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: 'rgba(255,100,100,0.8)',
              }}
            >
              Couldn’t load events preview. Open the full calendar on Luma instead.
            </div>
          )}

          {!isLoading && !error && filteredEvents.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: 'rgba(255,255,255,0.5)',
              }}
            >
              No upcoming events found. Check the full calendar on Luma.
            </div>
          )}

          {dayGroups.map(group => (
            <div key={group.label} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'rgba(255,255,255,0.6)',
                  marginTop: '4px',
                }}
              >
                {group.label}
              </div>
              {group.events.map(event => (
                <div
                  key={event.id}
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '12px',
                    padding: '12px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '8px',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#ffffff',
                        }}
                      >
                        {event.title}
                      </div>
                      <div
                        style={{
                          marginTop: '4px',
                          fontSize: '12px',
                          color: 'rgba(255,255,255,0.7)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px',
                        }}
                      >
                        {(event.startAt || event.date) && (
                          <span>
                            {new Date(event.startAt || event.date!).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                            })}{' '}
                            ·{' '}
                            {new Date(event.startAt || event.date!).toLocaleTimeString(undefined, {
                              hour: 'numeric',
                              minute: '2-digit',
                            })}{' '}
                            {event.timezone && (
                              <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                                {event.timezone}
                              </span>
                            )}
                          </span>
                        )}
                        {event.location && <span>{event.location}</span>}
                        {event.organizer && (
                          <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                            By {event.organizer}
                          </span>
                        )}
                      </div>
                    </div>
                    {event.superteam && (
                      <div
                        style={{
                          alignSelf: 'flex-start',
                          fontSize: '11px',
                          padding: '4px 8px',
                          borderRadius: '999px',
                          background: 'rgba(20,241,149,0.12)',
                          border: '1px solid rgba(20,241,149,0.35)',
                          color: '#14F195',
                        }}
                      >
                        {event.superteam}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={withClickSound(() => {
                      if (event.url) {
                        window.open(event.url, '_blank', 'noopener,noreferrer');
                      }
                    })}
                    style={{
                      alignSelf: 'flex-start',
                      marginTop: '4px',
                      fontSize: '12px',
                      color: '#9945FF',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: 0,
                    }}
                  >
                    View on Luma
                    <svg
                      style={{ width: '12px', height: '12px' }}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          ))}

          {/* Bottom \"Open full calendar\" button */}
          <button
            type="button"
            style={{
              ...buttonStyle,
              alignSelf: 'center',
              marginTop: '8px',
              marginBottom: '4px',
              padding: '8px 14px',
              fontSize: '13px',
            }}
            onClick={handleOpenLuma}
          >
            <span>Open full calendar on Luma</span>
            <svg
              style={{ width: '14px', height: '14px' }}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}
